// Analytics Visitor Tracking
class AnalyticsTracker {
  private endpoint: string
  private sessionId: string
  private lastActivity: number
  private isTracking: boolean = false
  
  constructor() {
    this.endpoint = '/backend/api/analytics.php'
    this.sessionId = this.generateSessionId()
    this.lastActivity = Date.now()
    
    // Initialize tracking
    this.init()
  }
  
  private generateSessionId(): string {
    return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
  }
  
  private async init() {
    // Check if analytics tracking is enabled (you can add a setting for this)
    const trackingEnabled = await this.isTrackingEnabled()
    
    if (!trackingEnabled) {
      return
    }
    
    this.isTracking = true
    
    // Wait for page to fully load before tracking
    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.trackPageView(), 500)
      })
    } else {
      setTimeout(() => this.trackPageView(), 500)
    }
    
    // Set up event listeners
    this.setupEventListeners()
    
    // Track session activity
    this.setupActivityTracking()
  }
  
  private async isTrackingEnabled(): Promise<boolean> {
    try {
      // You can implement a settings check here
      // For now, we'll assume tracking is always enabled
      return true
    } catch (error) {
      return false
    }
  }
  
  private setupEventListeners() {
    // Track page changes for SPAs
    window.addEventListener('popstate', () => {
      this.trackPageView()
    })
    
    // Track clicks on navigation elements
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      
      // Track navigation clicks
      if (target.closest('a[href^="/"]') || target.closest('[data-track="navigation"]')) {
        setTimeout(() => this.trackPageView(), 100)
      }
    })
    
    // Track when user leaves the page
    window.addEventListener('beforeunload', () => {
      this.trackPageLeave()
    })
  }
  
  private setupActivityTracking() {
    // Track user activity to determine session duration
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    
    const updateActivity = () => {
      this.lastActivity = Date.now()
    }
    
    activityEvents.forEach(eventType => {
      document.addEventListener(eventType, updateActivity, { passive: true })
    })
  }
  
  public async trackPageView() {
    if (!this.isTracking) return
    
    const data = {
      action: 'track',
      page_url: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer || '',
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language
    }
    
    try {
      await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      })
    } catch (error) {
      // Silent fail
    }
  }
  
  public async trackEvent(eventName: string, properties: Record<string, any> = {}) {
    if (!this.isTracking) return
    
    const data = {
      action: 'track_event',
      event_name: eventName,
      event_properties: properties,
      page_url: window.location.pathname,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    }
    
    try {
      await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      })
    } catch (error) {
    }
  }
  
  private trackPageLeave() {
    const sessionDuration = Date.now() - this.lastActivity
    
    // Only track if session was longer than 5 seconds
    if (sessionDuration > 5000) {
      const data = {
        action: 'track_session_end',
        session_id: this.sessionId,
        session_duration: sessionDuration,
        timestamp: new Date().toISOString()
      }
      
      // Use sendBeacon for reliable tracking on page unload
      if (navigator.sendBeacon) {
        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
          formData.append(key, value.toString())
        })
        navigator.sendBeacon(this.endpoint, formData)
      }
    }
  }
  
  // Public API for custom tracking
  public trackCustomEvent(eventName: string, properties?: Record<string, any>) {
    this.trackEvent(eventName, properties)
  }
  
  public trackConversion(type: string, value?: number) {
    this.trackEvent('conversion', { type, value })
  }
  
  public trackFormSubmission(formName: string, success: boolean = true) {
    this.trackEvent('form_submission', { form_name: formName, success })
  }
  
  public trackSearch(query: string, results: number) {
    this.trackEvent('search', { query, results })
  }
  
  public trackDownload(fileName: string, fileType: string) {
    this.trackEvent('download', { file_name: fileName, file_type: fileType })
  }
}

// Initialize analytics tracking
let analytics: AnalyticsTracker | null = null

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    analytics = new AnalyticsTracker()
  })
} else {
  analytics = new AnalyticsTracker()
}

// Expose analytics globally for custom tracking
declare global {
  interface Window {
    analytics?: AnalyticsTracker
  }
}

if (typeof window !== 'undefined') {
  window.analytics = analytics || undefined
}

// Export for module usage
export default AnalyticsTracker
export { analytics }